import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

export type FinancialsDailyPerformanceFilters = {
  start_date: string;
  end_date: string;
};

export type FinancialsDailyPoint = {
  date: string;
  deposits: number;
  withdrawals: number;
  revenue: number;
};

type RawDailyRow = {
  date?: string | null;
  deposit_amount?: number | string | null;
  withdrawal_amount?: number | string | null;
  revenue?: number | string | null;
};

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const buildDateRange = (start: string, end: string): string[] => {
  if (!start || !end) return [];
  const startDay = dayjs(start, ISO_DATE_FORMAT, true);
  const endDay = dayjs(end, ISO_DATE_FORMAT, true);
  if (!startDay.isValid() || !endDay.isValid()) return [];
  if (endDay.isBefore(startDay, "day")) return [];

  const dates: string[] = [];
  let cursor = startDay;
  while (!cursor.isAfter(endDay, "day")) {
    dates.push(cursor.format(ISO_DATE_FORMAT));
    cursor = cursor.add(1, "day");
  }
  return dates;
};

/** Maps API rows to chart points and fills missing days with zeros. */
export const parseFinancialsDailyResponse = (
  body: unknown,
  filters: FinancialsDailyPerformanceFilters,
): FinancialsDailyPoint[] => {
  const rawList = Array.isArray((body as { data?: unknown })?.data)
    ? ((body as { data: RawDailyRow[] }).data ?? [])
    : [];

  const byDate = new Map<string, FinancialsDailyPoint>();
  for (const row of rawList) {
    const date = typeof row?.date === "string" ? row.date : "";
    if (!date) continue;
    byDate.set(date, {
      date,
      deposits: coerceNumber(row.deposit_amount),
      withdrawals: coerceNumber(row.withdrawal_amount),
      revenue: coerceNumber(row.revenue),
    });
  }

  const denseDates = buildDateRange(filters.start_date, filters.end_date);
  if (denseDates.length > 0) {
    return denseDates.map(
      (date) =>
        byDate.get(date) ?? {
          date,
          deposits: 0,
          withdrawals: 0,
          revenue: 0,
        },
    );
  }

  return Array.from(byDate.values());
};

export const fetchFinancialsDailyPerformance = async (
  filters: FinancialsDailyPerformanceFilters,
): Promise<FinancialsDailyPoint[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.FINANCIALS_DAILY_PERFORMANCE, {
    params: filters,
  });
  return parseFinancialsDailyResponse(response?.data, filters);
};

export const useGetFinancialsDailyPerformance = (
  filters: FinancialsDailyPerformanceFilters,
) => {
  const { start_date, end_date } = filters;

  return useQuery<FinancialsDailyPoint[], Error>({
    queryKey: ["FINANCIALS_DAILY_PERFORMANCE", start_date, end_date],
    queryFn: () => fetchFinancialsDailyPerformance(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
